import React, { useEffect, useMemo, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import dataSearching from '../../assets/Data_Searching.json';
import { useEnvironment } from '../../utils/EnvironmentContext';
import { fetchTaskEvents } from '../../api/taskEvents';
import TaskEventList from '../TaskEventList';
import { fetchVoiceInsights } from '../../api/voiceInsights';
import { fetchVoiceInsightsMetrics } from '../../api/voiceInsightsMetrics';
import { getDutchQualityExplanation } from '../../utils/callQualityText';

export default function TaskEventsModal({ taskSid, startDate, onClose }) {
  const { env } = useEnvironment();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insights, setInsights] = useState(null);
  const [metricsIn, setMetricsIn] = useState(null);
  const [metricsOut, setMetricsOut] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' ? window.innerWidth < 1280 : true);
  const prevTaskSidRef = useRef(taskSid);
  const isTaskChanging = prevTaskSidRef.current !== taskSid;

  // Try to find Call SID from the task events' attributes
  const { callSid, accountSidForCall } = useMemo(() => {
    let foundCallSid = null;
    let foundAccountSid = null;
    for (const ev of events || []) {
      const raw = ev?.payload?.task_attributes || ev?.taskInfo?.attributes;
      let attrs = raw;
      if (typeof raw === 'string') {
        try { attrs = JSON.parse(raw); } catch { attrs = null; }
      }
      const sid = attrs?.call_sid || attrs?.conversations?.conversation_attribute_1 || null;
      if (!foundAccountSid && attrs?.account_sid) foundAccountSid = attrs.account_sid;
      if (!foundCallSid && sid && typeof sid === 'string' && sid.startsWith('CA')) foundCallSid = sid;
    }
    return { callSid: foundCallSid, accountSidForCall: foundAccountSid };
  }, [events]);

  // Quality evaluation helpers derived from Call Insights raw metrics
  const quality = useMemo(() => {
    if (!insights || !insights.insightsAvailable) return null;
    const raw = insights.raw || {};
    const sip = raw.sip_edge || {};
    const metrics = sip.metrics || {};
    const inbound = metrics.inbound || {};
    const outbound = metrics.outbound || {};
    // Twilio Insights SIP edge jitter values are reported in milliseconds (ms)
    const toMs = (v) => (typeof v === 'number' ? v : null);
    const fmtPct = (v) => (typeof v === 'number' && isFinite(v) ? `${(Math.round(v * 10) / 10).toFixed(1)}%` : '—');
    const fmtMs = (v) => (typeof v === 'number' ? `${(Math.round(v * 10) / 10).toFixed(1)} ms` : '—');
    const jitterInMax = toMs(inbound?.jitter?.max);
    const jitterOutMax = toMs(outbound?.jitter?.max);
    const lossInPct = typeof inbound?.packets_loss_percentage === 'number' ? inbound.packets_loss_percentage
      : (typeof inbound?.packets_lost === 'number' && typeof inbound?.packets_received === 'number' && (inbound.packets_lost + inbound.packets_received) > 0)
        ? (inbound.packets_lost / (inbound.packets_lost + inbound.packets_received)) * 100 : null;
    const lossOutPct = typeof outbound?.packets_loss_percentage === 'number' ? outbound.packets_loss_percentage
      : (typeof outbound?.packets_lost === 'number' && typeof outbound?.packets_sent === 'number' && (outbound.packets_lost + outbound.packets_sent) > 0)
        ? (outbound.packets_lost / (outbound.packets_lost + outbound.packets_sent)) * 100 : null;
    const mos = typeof insights.mos === 'number' ? insights.mos : null;
    const pddMs = raw?.properties?.pdd_ms ?? null;

    const evalSeverity = (value, warn, poor, invert = false) => {
      if (value == null) return 'unknown';
      if (!invert) {
        if (value >= poor) return 'poor';
        if (value >= warn) return 'warn';
        return 'good';
      } else {
        // invert means lower is worse (e.g., MOS)
        if (value <= poor) return 'poor';
        if (value <= warn) return 'warn';
        return 'good';
      }
    };

  // Thresholds are in milliseconds
  const sevJitterIn = evalSeverity(jitterInMax, 30, 200);
  const sevJitterOut = evalSeverity(jitterOutMax, 30, 200);
    const sevLossIn = evalSeverity(lossInPct, 1, 3);
    const sevLossOut = evalSeverity(lossOutPct, 1, 3);
    const sevMos = evalSeverity(mos, 3.6, 3.2, true);
    const sevPdd = evalSeverity(pddMs, 1000, 2000);

    const severities = [sevJitterIn, sevJitterOut, sevLossIn, sevLossOut, sevMos, sevPdd].filter(s => s !== 'unknown');
    const overall = severities.includes('poor') ? 'poor' : severities.includes('warn') ? 'warn' : (severities.length ? 'good' : 'unknown');

    return {
      overall,
      metrics: {
        jitterInMax,
        jitterOutMax,
        lossInPct,
        lossOutPct,
        mos,
        pddMs,
      },
      display: {
        jitterInMax: fmtMs(jitterInMax),
        jitterOutMax: fmtMs(jitterOutMax),
        lossInPct: fmtPct(lossInPct ?? NaN),
        lossOutPct: fmtPct(lossOutPct ?? NaN),
        mos: mos == null ? '—' : mos.toFixed(2),
        pddMs: pddMs == null ? '—' : `${pddMs} ms`,
      },
      severity: {
        jitterInMax: sevJitterIn,
        jitterOutMax: sevJitterOut,
        lossInPct: sevLossIn,
        lossOutPct: sevLossOut,
        mos: sevMos,
        pddMs: sevPdd,
      }
    };
  }, [insights]);

  const colorFor = (sev) => {
    switch (sev) {
      case 'poor': return '#991b1b';
      case 'warn': return '#92400e';
      case 'good': return '#166534';
      default: return '#374151';
    }
  };

  // Handle responsive layout (side-by-side vs stacked)
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Guard against flicker: start loading immediately on task change
    setLoading(true);
    setEvents([]);
    setInsights(null);
    setMetricsIn(null);
    setMetricsOut(null);
    setInsightsError('');
  }, [taskSid]);

  useEffect(() => {
    // After render, commit the new taskSid so next renders know the change is settled
    prevTaskSidRef.current = taskSid;
  }, [taskSid]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!taskSid) return;
      setLoading(true);
      setError('');
      try {
        const data = await fetchTaskEvents(taskSid, startDate, env);
        if (alive) setEvents(data.events || []);
      } catch (err) {
        if (alive) setError(err.message || 'Kon task events niet ophalen.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [taskSid, startDate, env]);

  // Auto-fetch Call Insights and metrics when we have a Call SID
  useEffect(() => {
    let alive = true;
    async function loadInsights() {
      if (!callSid) { setInsights(null); setMetricsIn(null); setMetricsOut(null); return; }
      setInsightsError('');
      setInsightsLoading(true);
      try {
        const data = await fetchVoiceInsights(callSid, env, accountSidForCall);
        if (alive) setInsights(data);
        // Fetch metrics in parallel but don't block UI on failures
        try {
          setMetricsLoading(true);
          const [mIn, mOut] = await Promise.all([
            fetchVoiceInsightsMetrics(callSid, env, accountSidForCall, { edge: 'sip_edge', direction: 'inbound', pageSize: 100 }),
            fetchVoiceInsightsMetrics(callSid, env, accountSidForCall, { edge: 'sip_edge', direction: 'outbound', pageSize: 100 }),
          ]);
          if (alive) { setMetricsIn(mIn); setMetricsOut(mOut); }
        } catch {
          // ignore metrics errors
        } finally {
          if (alive) setMetricsLoading(false);
        }
      } catch (e) {
        if (alive) setInsightsError(e.message || 'Kon Call Insights niet ophalen');
      } finally {
        if (alive) setInsightsLoading(false);
      }
    }
    loadInsights();
    return () => { alive = false; };
  }, [callSid, env, accountSidForCall]);

  const insightsCallSid = insights?.callSid || insights?.basicCall?.callSid || null;
  const awaitingNewCall = callSid && callSid !== insightsCallSid;
  const combinedLoading = isTaskChanging || loading || (callSid ? (awaitingNewCall || insights == null || insightsLoading || metricsLoading) : false);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 3000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflowY: 'auto', padding: 24
      }}
      role="dialog" aria-modal="true"
    >
      {combinedLoading ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 12px 48px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Lottie animationData={dataSearching} loop autoplay style={{ width: 200, height: 200 }} />
        </div>
      ) : (
      <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 12, alignItems: 'flex-start', width: 'auto', maxWidth: '96vw', margin: '24px 0' }}>
        <div
          style={{
            background: '#fff', borderRadius: 10, padding: 0,
            width: isNarrow ? '92vw' : 'auto',
            flex: isNarrow ? '0 0 auto' : '0 1 auto',
            minWidth: isNarrow ? undefined : 0,
            maxWidth: isNarrow ? '92vw' : 1200,
            boxShadow: '0 12px 48px rgba(0,0,0,0.35)',
            overflow: 'hidden'
          }}
        >
        <div style={{ padding: 20, maxHeight: '75vh', overflowY: 'auto' }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 18 }}>Task events</div>
            <div style={{ color: '#6b7280', fontSize: 12 }}>Task SID: {taskSid}</div>
            {startDate && (<div style={{ color: '#6b7280', fontSize: 12 }}>Vanaf: {startDate}</div>)}
          </div>
          {loading && <div style={{ padding: 12 }}>Bezig met laden…</div>}
          {error && <div style={{ padding: 12, color: 'red' }}>{error}</div>}
          {!loading && !error && (
            events && events.length ? <TaskEventList events={events} /> : <div style={{ padding: 12, color: '#6b7280' }}>Geen events gevonden voor deze task.</div>
          )}
        </div>
        </div>

        {/* Right side: Close bar + Call Insights container */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', width: isNarrow ? '92vw' : 560, maxWidth: 560, flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{
              width: '100%', marginBottom: 8,
              background: '#f79251', border: 'none',
              borderRadius: 8, padding: '10px 12px',
              fontSize: 14, fontWeight: 700, color: '#ffffff', cursor: 'pointer'
            }}
            aria-label="Sluiten"
            title="Sluiten"
          >
            Sluiten
          </button>
          <div
            style={{
              background: '#fff', borderRadius: 10, padding: 16,
              width: '100%', maxWidth: '100%',
              overflowY: 'visible',
              boxShadow: '0 12px 48px rgba(0,0,0,0.35)'
            }}
          >
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Call Insights</div>
          {!callSid && loading && (
            <div style={{ padding: 8 }}>Bezig met laden…</div>
          )}
          {!callSid && !loading && (
            <div style={{ color: '#6b7280' }}>Geen Call SID gevonden in deze task.</div>
          )}
          {callSid && !accountSidForCall && (
            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 6 }}>Account SID niet gevonden in task-attributen; val terug op huidig gekozen omgeving.</div>
          )}
          {(insightsLoading && callSid) && (
            <div style={{ padding: 8 }}>Bezig met laden…</div>
          )}
          {insightsError && (
            <div style={{ padding: 8, color: 'red' }}>{insightsError}</div>
          )}
          {!insightsLoading && !insightsError && insights && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
              <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Samenvatting</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                  <div><strong>Call SID</strong><div>{insights.callSid || insights.basicCall?.callSid}</div></div>
                  {insights.parentCallSid && <div><strong>Parent SID</strong><div>{insights.parentCallSid}</div></div>}
                  <div><strong>Van</strong><div>{insights.from || insights.basicCall?.from || '—'}</div></div>
                  <div><strong>Naar</strong><div>{insights.to || insights.basicCall?.to || '—'}</div></div>
                  <div><strong>Richting</strong><div>{insights.direction || insights.basicCall?.direction || '—'}</div></div>
                  <div><strong>Duur (s)</strong><div>{(insights.duration ?? insights.basicCall?.duration) ?? '—'}</div></div>
                  {!insights.insightsAvailable && (
                    <div style={{ gridColumn: '1 / -1', color: '#6b7280' }}>
                      Call Insights is niet beschikbaar voor deze call. Basis call-gegevens worden getoond.
                    </div>
                  )}
                  {insights.insightsAvailable && <>
                    <div><strong>Beëindigd door</strong><div>{insights.hangupBy || '—'}</div></div>
                    <div><strong>Reden</strong><div>{insights.endReason || '—'}</div></div>
                    {insights.mediaRegion && <div><strong>Media regio</strong><div>{insights.mediaRegion}</div></div>}
                    {insights.codec && <div><strong>Codec</strong><div>{insights.codec}</div></div>}
                    {insights.mos && <div><strong>MOS</strong><div>{insights.mos}</div></div>}
                  </>}
                </div>
              </div>
              {quality && (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontWeight: 600 }}>Gespreksqualiteit</div>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999,
                      background: quality.overall === 'poor' ? '#fee2e2' : quality.overall === 'warn' ? '#fef3c7' : '#dcfce7',
                      color: quality.overall === 'poor' ? '#991b1b' : quality.overall === 'warn' ? '#92400e' : '#166534',
                      fontSize: 12
                    }}>
                      {quality.overall === 'poor' ? 'Slecht' : quality.overall === 'warn' ? 'Verminderd' : quality.overall === 'good' ? 'Goed' : 'Onbekend'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                    <div><strong>Inbound jitter (max)</strong><div style={{ color: colorFor(quality.severity.jitterInMax) }}>{quality.display.jitterInMax}</div></div>
                    <div><strong>Outbound jitter (max)</strong><div style={{ color: colorFor(quality.severity.jitterOutMax) }}>{quality.display.jitterOutMax}</div></div>
                    <div><strong>Inbound packet loss</strong><div style={{ color: colorFor(quality.severity.lossInPct) }}>{quality.display.lossInPct}</div></div>
                    <div><strong>Outbound packet loss</strong><div style={{ color: colorFor(quality.severity.lossOutPct) }}>{quality.display.lossOutPct}</div></div>
                    <div><strong>MOS</strong><div style={{ color: colorFor(quality.severity.mos) }}>{quality.display.mos}</div></div>
                    <div><strong>Post-dial delay</strong><div style={{ color: colorFor(quality.severity.pddMs) }}>{quality.display.pddMs}</div></div>
                    {insights?.annotation && <>
                      <div><strong>Answered by</strong><div>{insights.annotation.answeredBy || '—'}</div></div>
                      <div><strong>Connectivity issue</strong><div>{insights.annotation.connectivityIssue || '—'}</div></div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Quality issues</strong>
                        <div>{Array.isArray(insights.annotation.qualityIssues) && insights.annotation.qualityIssues.length ? insights.annotation.qualityIssues.join(', ') : '—'}</div>
                      </div>
                      <div><strong>Spam</strong><div>{insights.annotation.spam === true ? 'Ja' : insights.annotation.spam === false ? 'Nee' : '—'}</div></div>
                      <div><strong>Call score</strong><div>{insights.annotation.callScore ?? '—'}</div></div>
                      <div style={{ gridColumn: '1 / -1' }}><strong>Comment</strong><div>{insights.annotation.comment || '—'}</div></div>
                    </>}
                  </div>
                  {(metricsIn?.series?.length || metricsOut?.series?.length) && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Jitter over time (ms)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 2 }}>Inbound</div>
                          <Sparkline data={(metricsIn?.series || []).map(s => s.jitterMs).filter(v => typeof v === 'number')} color="#2563eb" />
                        </div>
                        <div>
                          <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 2 }}>Outbound</div>
                          <Sparkline data={(metricsOut?.series || []).map(s => s.jitterMs).filter(v => typeof v === 'number')} color="#10b981" />
                        </div>
                      </div>
                      {(metricsIn?.edgeUsed && metricsIn.edgeUsed !== 'sip_edge') || (metricsOut?.edgeUsed && metricsOut.edgeUsed !== 'sip_edge') ? (
                        <div style={{ color: '#6b7280', fontSize: 11, marginTop: 6 }}>
                          Let op: metrics afkomstig van edge "{metricsIn?.edgeUsed || metricsOut?.edgeUsed}" wegens ontbreken van SIP-edge gegevens.
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              {/* Dutch explanation outside the quality container, directly underneath */}
              {quality && (() => {
                const expl = getDutchQualityExplanation(quality, { direction: insights?.direction, hangupBy: insights?.hangupBy, mos: insights?.mos, pddMs: quality?.metrics?.pddMs });
                if (!expl) return null;
                return (
                  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700 }}>Uitleg</div>
                    </div>
                    <p style={{ color: '#111827', lineHeight: 1.5, margin: '4px 0 8px 0', fontSize: 13 }}>{expl.summary}</p>
                    {Array.isArray(expl.causes) && expl.causes.length > 0 && (
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Mogelijke oorzaken</div>
                        <ul style={{ margin: 0, paddingLeft: 18, color: '#374151', fontSize: 13 }}>
                          {expl.causes.map((c, idx) => (<li key={idx} style={{ marginBottom: 4 }}>{c}</li>))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}
              {insights.insightsAvailable && insights.events && insights.events.length > 0 && (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Belangrijke events</div>
                  <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>Tijd</th>
                          <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>Event</th>
                          <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>Edge</th>
                          <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>Niveau</th>
                          <th style={{ textAlign: 'left', padding: 6, borderBottom: '1px solid #e5e7eb' }}>Omschrijving</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insights.events.map(ev => (
                          <tr key={ev.sid}>
                            <td style={{ padding: 6, borderBottom: '1px solid #eef2f7' }}>{ev.timestamp || '—'}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eef2f7' }}>{ev.name || '—'}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eef2f7' }}>{ev.edge || '—'}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eef2f7' }}>{ev.level || '—'}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eef2f7' }}>{ev.description || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function Sparkline({ data, width = 220, height = 40, color = '#2563eb' }) {
  const padding = 2;
  const w = width - padding * 2;
  const h = height - padding * 2;
  if (!data || data.length === 0) return <div style={{ height }} />;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = w / Math.max(1, data.length - 1);
  const points = data.map((v, i) => {
    const x = padding + i * step;
    const y = padding + h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} role="img" aria-label="sparkline">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
    </svg>
  );
}
